import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, FileText } from "lucide-react";
import FileUpload from "@/components/chat/FileUpload";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

type Persona = "travel" | "health" | "meals" | "errands" | "home" | "work";

interface ChatInputProps {
  onSendMessage: (message: string, files: Array<{ name: string; url: string; type: string; file?: File; fileType?: string; fileName?: string }>) => Promise<void>;
  isLoading: boolean;
  selectedPersona: Persona | null;
}

const ChatInput = ({ onSendMessage, isLoading, selectedPersona }: ChatInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; url: string; type: string; file?: File; fileType?: string; fileName?: string }>>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Prevent viewport jumping on mobile when keyboard appears
  useEffect(() => {
    if (isMobile) {
      // Store original viewport height
      const originalViewportHeight = window.visualViewport?.height || window.innerHeight;
      
      const handleViewportChange = () => {
        // Prevent body from being pushed up when keyboard appears
        if (window.visualViewport) {
          const currentHeight = window.visualViewport.height;
          const heightDifference = originalViewportHeight - currentHeight;
          
          if (heightDifference > 150) { // Keyboard is probably open
            document.body.style.transform = `translateY(0px)`;
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
          } else {
            document.body.style.transform = '';
            document.body.style.position = '';
            document.body.style.width = '';
          }
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportChange);
      }

      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportChange);
        }
        // Clean up styles
        document.body.style.transform = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
  }, [isMobile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    await onSendMessage(inputValue, uploadedFiles);
    setInputValue("");
    setUploadedFiles([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaFocus = (e: React.FocusEvent) => {
    if (isMobile) {
      // Prevent default scroll behavior
      e.preventDefault();
      
      // Small delay to ensure the keyboard animation doesn't interfere
      setTimeout(() => {
        // Ensure we stay in the current scroll position
        window.scrollTo({ top: window.scrollY, behavior: 'instant' });
      }, 100);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleFileUpload = async () => {
    if (selectedFile) {
      try {
        const newFile = {
          name: selectedFile.name,
          url: URL.createObjectURL(selectedFile),
          type: selectedFile.type,
          file: selectedFile
        };
        setUploadedFiles(prev => [...prev, newFile]);
        setSelectedFile(null);
        
        // Removed the success toast - the UI already shows clear guidance
      } catch (error) {
        console.error("Error processing file:", error);
        
        // Check if it's a file size error
        if (error instanceof Error && error.message.includes('413')) {
          toast({
            variant: "destructive",
            title: "File Too Large",
            description: "The file you're trying to upload is too large for the server. Please try a smaller file.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to process file. Please try again.",
          });
        }
      }
    }
  };

  const handleUrlUpload = async (url: string) => {
    try {
      const newWebContent = {
        name: `Web: ${url}`,
        url: url,
        type: 'web',
        fileType: 'web',
        fileName: url  // Send the URL as fileName for n8n
      };
      setUploadedFiles(prev => [...prev, newWebContent]);
      
      toast({
        title: "URL added",
        description: "Web content ready for processing",
      });
    } catch (error) {
      console.error("Error processing URL:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to process URL. Please try again.",
      });
    }
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="chat-input-area p-4 sm:p-5 flex-shrink-0">
      {uploadedFiles.length > 0 && (
        <div className="mb-3 sm:mb-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 bg-primary/10 border border-primary/20 px-3 py-2 rounded-xl text-sm backdrop-blur-sm">
                <span className="truncate max-w-[120px] sm:max-w-none text-foreground">{file.name}</span>
                <button
                  onClick={() => removeUploadedFile(index)}
                  className="text-destructive hover:text-destructive/80 transition-colors font-medium"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-xl text-sm backdrop-blur-sm">
            <FileText className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-foreground font-medium">File ready! Please tell me what you'd like me to do with it (analyze, summarize, extract data, etc.)</span>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex gap-1 sm:gap-2">
        <Textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={handleTextareaFocus}
          placeholder={uploadedFiles.length > 0 
            ? "Tell me what to do with your uploaded file..." 
            : "Type your message..."
          }
          className="flex-1 min-h-[40px] sm:min-h-[44px] max-h-24 sm:max-h-32 resize-none bg-input border-border text-foreground text-sm sm:text-base rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all backdrop-blur-sm"
          disabled={isLoading}
        />
        <Button 
          type="submit" 
          disabled={isLoading || !inputValue.trim()}
          size="sm"
          className="h-9 w-9 sm:h-10 sm:w-10 p-0"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
          ) : (
            <Send className="h-3 w-3 sm:h-4 sm:w-4" />
          )}
        </Button>
      </form>
      
      <div className="mt-2">
        <FileUpload 
          selectedFile={selectedFile}
          isLoading={isLoading}
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          onFileUpload={handleFileUpload}
          onUrlUpload={handleUrlUpload}
        />
      </div>
    </div>
  );
};

export default ChatInput;
